/**
 * One file, everything the studio knows about it, and everything they may do to it.
 *
 * One dialog per row rather than four buttons on every row: a hundred rows with four
 * controls each is four hundred controls, and all four writes need a typed reason or
 * note anyway (`ReasonSchema` is `min 3`, and `files_request_review` takes the note
 * the client will read). So the row carries *Manage* and this carries the rest.
 *
 * **A control the API would refuse is not shown** — `.docs/24`: "A control shown to
 * somebody the API will refuse is worse than no control." Each of the four is offered
 * exactly where `modules/files/service.ts` accepts it:
 *
 *   Release        a held file that is not archived. `markReleased` throws `Conflict`
 *                  on an archived one and there is no un-archive.
 *   Hold           a released file. Idempotent on a held one, so it is pointless.
 *   Request review a released, un-archived **deliverable**. `requestReview` refuses
 *                  every other kind, and refuses a held file outright — "a review
 *                  request is a link to something they can see".
 *   Archive        anything not already archived.
 *
 * **The review record is here because it is nowhere else.** The client's answer —
 * what they said, and when — was written by session 16 and read by no screen in
 * either portal (R2-11), so a studio member who got a *changes requested* mail had to
 * open the audit log to find out what the changes were. Both halves are printed: the
 * question `files_request_review` asked and the answer that came back.
 *
 * The three `!` actions stay `!`. Nothing here weakens a scope — `admin` is not
 * grantable to an agent by CHECK, and a staff session holds it because a staff session
 * holds every scope.
 */

import { useState } from 'react'

import type { FileView } from '@fareast/shared'

import { DownloadLink } from '../../client/files/DownloadLink.js'
import { Button } from '../../../components/portal/Button.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Row, Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Pill } from '../../../components/portal/Status.js'
import { DateText, Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { useFormat } from '../../../format/useFormat.js'

export type FileDialogProps = {
  readonly file: FileView
  /** Run after any of the four writes lands, so the page can refresh the summary. */
  readonly onDone: () => void
}

export function FileDialog({ file, onDone }: FileDialogProps) {
  const format = useFormat()
  const [open, setOpen] = useState(false)

  const archived = file.archived_at !== null
  const isDeliverable = file.kind === 'deliverable'

  /*
   * Closes this dialog when the inner one has run.
   *
   * `ActionDialog` closes itself; this one has to be told, or a release leaves a
   * panel open over a file whose state it is now printing wrongly.
   */
  const ran = () => {
    setOpen(false)
    onDone()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={file.title}
      trigger={<Button size="sm">Manage</Button>}
    >
      <Stack>
        <Kv bare>
          <KvRow k="File" v={<Small>{file.name}</Small>} />
          <KvRow k="Kind" v={<Small>{file.kind}</Small>} />
          {file.period === null ? null : (
            <KvRow k="Month" v={<Small>{format.monthLong(file.period)}</Small>} />
          )}
          <KvRow k="Size" v={<Num>{format.bytes(file.size)}</Num>} />
          <KvRow k="Added" v={<DateText value={file.created_at} />} />
          <KvRow
            k="Released"
            v={
              archived ? (
                <Pill>Archived</Pill>
              ) : file.released_at === null ? (
                <Pill state="need">Held</Pill>
              ) : (
                <DateText value={file.released_at} />
              )
            }
          />
        </Kv>

        <ReviewRecord file={file} />

        <Row>
          <DownloadLink id={file.id} label={`Download ${file.title}`} />
        </Row>

        <Row>
          {archived || file.released ? null : (
            <ActionDialog
              action="files_release"
              label="Release"
              title={`Release ${file.title} to the client`}
              submitLabel="Release it"
              variant="ochre"
              size="sm"
              fixed={{ file_id: file.id }}
              onDone={ran}
            >
              {/*
                `modules/files/jobs.ts`: "A release is one-way … nothing re-locks a
                handover a client has downloaded, and pretending otherwise would be a
                gate with a hole in it rather than a gate." Hold exists and takes the
                link away; it does not take the copy back. The confirmation says which.
              */}
              This is one way. From the moment you release it the client can download it, and
              holding it again later takes the link away and not the copy. The reason goes on the
              audit row and into the file.released event.
            </ActionDialog>
          )}

          {archived || !file.released ? null : (
            <ActionDialog
              action="files_hold"
              label="Hold"
              title={`Hold ${file.title} back`}
              submitLabel="Hold it"
              size="sm"
              fixed={{ file_id: file.id }}
              onDone={ran}
            >
              The client’s next request for it is a 404. Use this when something went out that
              should not have and is still expected to go out later; archive it instead when it is
              wrong rather than early. Nobody is emailed.
            </ActionDialog>
          )}

          {archived || !file.released || !isDeliverable ? null : (
            <ActionDialog
              action="files_request_review"
              label="Ask for a review"
              title={`Ask the client to review ${file.title}`}
              submitLabel="Send it"
              size="sm"
              fixed={{ file_id: file.id }}
              onDone={ran}
            >
              Everybody on the account with sign-off mail switched on is emailed, and a “Chase
              sign-off” task is raised if nobody answers in five days. Asking again starts the clock
              over and clears whatever they said last time — so say what to look at.
            </ActionDialog>
          )}

          {archived ? null : (
            <ActionDialog
              action="files_archive"
              label="Archive"
              title={`Archive ${file.title}`}
              submitLabel="Archive it"
              size="sm"
              fixed={{ file_id: file.id }}
              onDone={ran}
            >
              It disappears from every list and every bundle the client sees, and it is not meant to
              come back — there is no un-archive. Nothing is deleted: staff can still download it
              and the row still proves the work was delivered in the month it was billed for.
            </ActionDialog>
          )}
        </Row>

        {archived ? (
          <Prose>
            <Muted>
              This file is archived, so the client no longer sees it anywhere. Archiving is one way;
              the row and the bytes stay as the record.
            </Muted>
          </Prose>
        ) : null}
      </Stack>
    </Dialog>
  )
}

/* ------------------------------------------------------------- the review record */

/**
 * What was asked, and what came back.
 *
 * Both halves, and this is the only screen in either portal that prints them
 * together. The client's own page shows the question while it is open and their
 * answer once they have given one (`pages/client/files/MonthFiles.tsx` says why it
 * drops the question at that point); for the studio the pair *is* the record, because
 * "changes requested" without the words is a mail that tells you nothing.
 */
function ReviewRecord({ file }: { readonly file: FileView }) {
  if (file.review === 'none') {
    return (
      <Prose>
        <Muted>Nobody has asked this client to review this file.</Muted>
      </Prose>
    )
  }

  return (
    <Kv bare>
      <KvRow
        k="We asked"
        v={
          file.review_requested_at === null ? (
            <Small>—</Small>
          ) : (
            <DateText value={file.review_requested_at} />
          )
        }
      />
      {file.review_request_note === '' ? null : (
        <KvRow k="For" v={<Small>{file.review_request_note}</Small>} />
      )}
      <KvRow
        k="They said"
        v={
          file.review === 'requested' ? (
            <Pill state="need">Still waiting</Pill>
          ) : file.review === 'approved' ? (
            <Small>Approved</Small>
          ) : (
            <Pill state="need">Changes asked for</Pill>
          )
        }
      />
      {file.reviewed_at === null ? null : (
        <KvRow k="Answered" v={<DateText value={file.reviewed_at} />} />
      )}
      {file.review_note === '' ? null : (
        <KvRow k="In their words" v={<Small>{file.review_note}</Small>} />
      )}
    </Kv>
  )
}
